import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

/**
 * KpiCarouselDots
 * Global, view-only helper that draws swipe "dot" indicators under every
 * `.kpi-carousel` row — but ONLY on phones (<= 767px), matching the
 * mobile carousel CSS. It discovers carousels in the DOM, follows page
 * scroll, and highlights the active card. Renders via a portal into
 * document.body (absolute positioning) so it never interferes with the
 * page's React tree or desktop layout.
 */
export const KpiCarouselDots = () => {
  const [items, setItems] = useState([]);
  const rafRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const GAP = 12; // matches CSS gap: 0.75rem

    const compute = () => {
      if (window.innerWidth > 767) {
        setItems((prev) => (prev.length ? [] : prev));
        return;
      }
      const carousels = Array.from(document.querySelectorAll(".kpi-carousel"));
      const next = carousels
        .map((el) => {
          const rect = el.getBoundingClientRect();
          const count = el.children.length;
          const first = el.children[0];
          const cardW = first ? first.getBoundingClientRect().width : 1;
          const active = Math.round(el.scrollLeft / (cardW + GAP));
          return {
            count,
            active: Math.max(0, Math.min(count - 1, active)),
            left: rect.left + window.scrollX,
            top: rect.bottom + window.scrollY + 6,
            width: rect.width,
          };
        })
        .filter((x) => x.count > 1);
      setItems(next);
    };

    const schedule = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(compute);
    };

    compute();
    // capture=true so we also catch horizontal scroll on the carousels themselves
    window.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);
    const mo = new MutationObserver(schedule);
    mo.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(compute, 800); // safety for late/async renders & route changes

    return () => {
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      mo.disconnect();
      clearInterval(interval);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  if (!items.length) return null;

  return createPortal(
    <>
      {items.map((it, idx) => (
        <div
          key={idx}
          data-testid="kpi-carousel-dots"
          className="flex justify-center gap-1.5"
          style={{
            position: "absolute",
            left: it.left,
            top: it.top,
            width: it.width,
            pointerEvents: "none",
            zIndex: 30,
          }}
        >
          {Array.from({ length: it.count }).map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === it.active
                  ? "w-5 bg-slate-600 dark:bg-slate-300"
                  : "w-1.5 bg-slate-300 dark:bg-slate-500"
              }`}
            />
          ))}
        </div>
      ))}
    </>,
    document.body
  );
};

export default KpiCarouselDots;
